import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { enqueueSnackbar } from 'notistack';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    Orientations,
    PlaneView,
    PlaneViewType,
} from '~/engine/view/views/PlaneView';
import { View } from '../../../view/View';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { MOUSE_BUTTON_LEFT, MOUSE_BUTTONS_NONE } from '../../MouseButton';
import { Tool } from '../../Tool';
import {
    LineDeformAction,
    LineDeformActionState,
    SliceAxis,
} from '../../actions/contouring-actions/LineDeformAction';

/**
 * When provided to the cursor, the endpoint dots are placed
 * using the original (undeformed) polygon's arc lengths, then
 * mapped to the corresponding positions on the deformed polygon.
 */
interface DeformFalloffInfo {
    /** Index of the deformed polygon within the slice polygons array. */
    polygonIndex: number;
    /** Index of the anchor vertex on the polygon. */
    anchorIndex: number;
    /** Undeformed vertex positions for arc-length computation. */
    originalPolygon: vec3[];
}
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { marchingSquares } from '~/engine/core/utils/marchingSquares';
import { LayerBit } from '~/engine/view/layers/LayerBit';

const MIN_SPREAD_PERCENT = 1;
const MAX_SPREAD_PERCENT = 100;

/**
 * Maximum Euclidean distance (mm) from the mouse to a
 * polygon vertex for the cursor to snap to that polygon.
 */
const SNAP_RADIUS_MM = 15;

/**
 * {@link LineDeformTool} applies a 2D deformation to an existing
 * ROI's SDF. Delegates stroke state management to
 * {@link LineDeformAction} and {@link LineDeformActionState}.
 *
 * This version extracts polygon outlines via marching squares
 * and deforms them using Gaussian arc-length decay, then
 * re-renders the SDF from the deformed polygons.
 */
/** Cached polygon data for a single slice. */
interface SlicePolygonCache {
    structure: RegionOfInterest;
    sdfVersion: number;
    sliceIndex: number;
    sliceAxis: SliceAxis;
    polygons: vec3[][];
}

export class LineDeformTool extends Tool {
    public readonly toolType = 'lineDeform';

    private static readonly SIZE_MODIFIER_KEYS = ModifierKeys.SHIFT;
    private _sliceCache?: SlicePolygonCache;

    private readonly structure: RegionOfInterest;
    private readonly image: ImageObject;
    private _spreadPercent: number;
    private readonly spreadChangedCallback: (spreadPercent: number) => void;

    private _cursor?: DeformCanvasCursor;
    private _deformAction?: LineDeformAction;
    private _deformState?: LineDeformActionState;
    private _activeView?: PlaneView;
    private _activeSliceIndex?: number;
    private _activeSliceAxis?: SliceAxis;
    private _lastCssCursor = '';

    public constructor(
        image: ImageObject,
        structure: RegionOfInterest,
        spreadPercent: number,
        spreadChangedCallback: (spreadPercent: number) => void
    ) {
        super();
        this.image = image;
        this.structure = structure;
        this._spreadPercent = Math.max(
            MIN_SPREAD_PERCENT,
            Math.min(MAX_SPREAD_PERCENT, spreadPercent)
        );
        this.spreadChangedCallback = spreadChangedCallback;
    }

    public get spreadPercent(): number {
        return this._spreadPercent;
    }

    public set spreadPercent(value: number) {
        const clamped = Math.max(
            MIN_SPREAD_PERCENT,
            Math.min(MAX_SPREAD_PERCENT, value)
        );
        if (clamped !== this._spreadPercent) {
            this._spreadPercent = clamped;
            this._cursor?.setSpreadPercent(this._spreadPercent);
        }
    }

    public override onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (modifierKeys.equals(LineDeformTool.SIZE_MODIFIER_KEYS)) {
            let step: number;
            if (this._spreadPercent <= 10) {
                step = 1;
            } else if (this._spreadPercent <= 30) {
                step = 2;
            } else if (this._spreadPercent <= 60) {
                step = 5;
            } else {
                step = 10;
            }
            this.spreadPercent = this._spreadPercent + delta * step;
            this._cursor?.setSpreadPercent(this._spreadPercent);
            const wheelAxis = LineDeformTool.viewSliceAxis(view);
            const sliceInfo =
                wheelAxis !== undefined
                    ? this.getSliceInfo(coordinates, view, wheelAxis)
                    : undefined;
            const polygons =
                sliceInfo !== undefined
                    ? this.getSlicePolygons(sliceInfo.index, sliceInfo.axis)
                    : undefined;
            this._cursor?.draw(view, coordinates, polygons);
            this.spreadChangedCallback(this._spreadPercent);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this.structure.canModify === false) {
            enqueueSnackbar(
                'Modifying approved structures is not allowed.' +
                    ' Unapprove the structure to make changes.',
                { variant: 'warning' }
            );
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const sliceAxis = LineDeformTool.viewSliceAxis(view);
        if (
            button === MOUSE_BUTTON_LEFT &&
            sliceAxis !== undefined &&
            view.type === PlaneViewType.Primary
        ) {
            view.snapToImagePixel();
            const sliceInfo = this.getSliceInfo(coordinates, view, sliceAxis);
            if (sliceInfo === undefined) {
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_NOT_HANDLED
                );
            }
            const polygons = this.getSlicePolygons(
                sliceInfo.index,
                sliceInfo.axis
            );
            this._deformAction = new LineDeformAction(
                this.structure,
                this.image,
                this._spreadPercent,
                sliceInfo.index,
                sliceInfo.axis,
                polygons
            );
            this._deformState = this._deformAction.startAction(
                view,
                coordinates
            );
            this._activeView = view;
            this._activeSliceIndex = sliceInfo.index;
            this._activeSliceAxis = sliceInfo.axis;
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        if (this.isActionInProgress()) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: PlaneView
    ): EventHandlerResult {
        if (buttons === MOUSE_BUTTONS_NONE && this._deformState) {
            return this.finishStroke();
        }

        if (this._deformState) {
            this._deformState.onMouseMove(coordinates);
            const polygons =
                this._activeSliceIndex !== undefined &&
                this._activeSliceAxis !== undefined
                    ? this.getSlicePolygons(
                          this._activeSliceIndex,
                          this._activeSliceAxis
                      )
                    : undefined;
            const origPoly = this._deformState.activeOriginalPolygon;
            const polyIdx = this._deformState.activePolygonIndex;
            const anchorIdx = this._deformState.activeAnchorIndex;
            const falloffInfo =
                origPoly !== undefined &&
                polyIdx !== undefined &&
                anchorIdx !== undefined
                    ? {
                          polygonIndex: polyIdx,
                          anchorIndex: anchorIdx,
                          originalPolygon: origPoly,
                      }
                    : undefined;
            this._cursor?.draw(view, coordinates, polygons, falloffInfo);
            this.setCssCursor(view, 'grabbing');
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }

        // Cursor hover
        const hoverAxis = LineDeformTool.viewSliceAxis(view);
        if (
            view instanceof PlaneView &&
            hoverAxis !== undefined &&
            this.structure.canModify &&
            view.type === PlaneViewType.Primary
        ) {
            if (!this._cursor) {
                this._cursor = new DeformCanvasCursor(
                    this._spreadPercent,
                    this.structure.color
                );
            }
            const sliceInfo = this.getSliceInfo(coordinates, view, hoverAxis);
            const polygons =
                sliceInfo !== undefined
                    ? this.getSlicePolygons(sliceInfo.index, sliceInfo.axis)
                    : undefined;
            const nearContour = this._cursor.draw(view, coordinates, polygons);
            this.setCssCursor(view, nearContour ? 'grab' : 'default');
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2,
        _view: View
    ): EventHandlerResult {
        if (this._deformState) {
            return this.finishStroke();
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view: View
    ): EventHandlerResult {
        if (key === 'Escape' && this._deformState) {
            this._deformState.cancel();
            this._deformState = undefined;
            this._deformAction = undefined;
            this._activeView = undefined;
            this._activeSliceIndex = undefined;
            this._activeSliceAxis = undefined;
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true,
                new VolumeViewUpdateEvent(
                    this.structure.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override isActionInProgress(): boolean {
        return this._deformState !== undefined;
    }

    public override getDrawingView(): PlaneView | undefined {
        return this.isActionInProgress() ? this._activeView : undefined;
    }

    public override onActivate(): EventHandlerResult {
        this.expandToImageSize();
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onDeactivate(): EventHandlerResult {
        if (this._deformState && this._deformAction) {
            const result = this._deformAction.stopAction(this._deformState);
            if (this._activeView) {
                this.resetCssCursor(this._activeView);
            }
            this._deformState = undefined;
            this._deformAction = undefined;
            this._activeView = undefined;
            this._activeSliceIndex = undefined;
            this._activeSliceAxis = undefined;
            this._cursor?.clear();
            this._cursor = undefined;
            return result;
        }
        if (this._cursor?.lastView) {
            this.resetCssCursor(this._cursor.lastView);
        }
        this._cursor?.clear();
        this._cursor = undefined;
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public override dispose(): void {
        this.cropTextureToContentBounds();
    }

    private setCssCursor(view: PlaneView, cursor: string): void {
        if (cursor !== this._lastCssCursor && view.canvas) {
            view.canvas.style.cursor = cursor;
            this._lastCssCursor = cursor;
        }
    }

    private resetCssCursor(view: PlaneView): void {
        if (view.canvas) {
            view.canvas.style.cursor = 'default';
        }
        this._lastCssCursor = '';
    }

    private cropTextureToContentBounds(): void {
        this._sliceCache = undefined;
        this.structure.texture?.cropToContentBounds();
    }

    private finishStroke(): EventHandlerResult {
        if (!this._deformState || !this._deformAction) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const result = this._deformAction.stopAction(this._deformState);
        this._deformState = undefined;
        this._deformAction = undefined;
        this._activeView = undefined;
        this._activeSliceIndex = undefined;
        this._activeSliceAxis = undefined;

        // Invalidate the slice cache so the next stroke
        // re-extracts from the updated SDF.
        this._sliceCache = undefined;

        return result;
    }

    /** Expand the texture to cover the full image grid. */
    private expandToImageSize(): void {
        const texture = this.structure.texture;
        if (!texture || texture.isEmpty) {
            return;
        }
        texture.expandToImageGrid();
    }

    /** Map a view orientation to the corresponding slice axis. */
    private static viewSliceAxis(view: PlaneView): SliceAxis | undefined {
        switch (view.label) {
            case Orientations.TRANSVERSAL_ORIENTATION:
                return 2;
            case Orientations.CORONAL_ORIENTATION:
                return 1;
            case Orientations.SAGITTAL_ORIENTATION:
                return 0;
            default:
                return undefined;
        }
    }

    /**
     * Determine the voxel index along the given axis for a
     * click in client coordinates. Returns undefined when
     * the texture is empty or the click falls outside.
     */
    private getSliceInfo(
        coordinates: ReadonlyVec2,
        view: PlaneView,
        axis: SliceAxis
    ): { index: number; axis: SliceAxis } | undefined {
        const texture = this.structure.texture;
        if (!texture || texture.isEmpty) {
            return undefined;
        }
        const grid = texture.textureGrid;
        const patientPos = view.clientToPatientCoordinates(coordinates);
        const voxel = grid.toVoxelSpace(patientPos);
        const idx = Math.floor(voxel[axis]);
        const axisSize = [grid.sizeX, grid.sizeY, grid.sizeZ][axis];
        if (idx < 0 || idx >= axisSize) {
            return undefined;
        }
        return { index: idx, axis };
    }

    /**
     * Return cached polygons for the given slice if the
     * structure and SDF version match, otherwise extract
     * fresh polygons for that single slice.
     */
    private getSlicePolygons(
        sliceIndex: number,
        sliceAxis: SliceAxis
    ): vec3[][] | undefined {
        const cache = this._sliceCache;
        if (
            cache &&
            cache.structure === this.structure &&
            cache.sdfVersion === this.structure.sdfVersion &&
            cache.sliceIndex === sliceIndex &&
            cache.sliceAxis === sliceAxis
        ) {
            return cache.polygons;
        }
        const polygons = this.extractSlicePolygons(sliceIndex, sliceAxis);
        if (polygons) {
            this._sliceCache = {
                structure: this.structure,
                sdfVersion: this.structure.sdfVersion,
                sliceIndex,
                sliceAxis,
                polygons,
            };
        }
        return polygons;
    }

    /**
     * Extract polygon outlines from a single slice of the
     * structure's SDF via marching squares, converting from
     * voxel space to patient space.
     */
    private extractSlicePolygons(
        sliceIndex: number,
        sliceAxis: SliceAxis
    ): vec3[][] | undefined {
        const texture = this.structure.texture;
        if (!texture || texture.isEmpty) {
            return undefined;
        }

        const grid = texture.textureGrid;
        const sdf = texture.signedDistanceField;
        const sizeX = grid.sizeX;
        const sizeY = grid.sizeY;
        const sizeZ = grid.sizeZ;
        const threshold = ContourTexture.SDF_THRESHOLD;

        let sliceData: Uint8Array | ArrayLike<number>;
        let width: number;
        let height: number;

        if (sliceAxis === 2) {
            // Transversal: XY plane at z = sliceIndex.
            sliceData = sdf.pixels[sliceIndex];
            width = sizeX;
            height = sizeY;
        } else if (sliceAxis === 1) {
            // Coronal: XZ plane at y = sliceIndex.
            width = sizeX;
            height = sizeZ;
            const buf = new Uint8Array(width * height);
            for (let z = 0; z < sizeZ; z++) {
                const zSlice = sdf.pixels[z];
                for (let x = 0; x < sizeX; x++) {
                    buf[z * width + x] = zSlice[sliceIndex * sizeX + x];
                }
            }
            sliceData = buf;
        } else {
            // Sagittal: YZ plane at x = sliceIndex.
            width = sizeY;
            height = sizeZ;
            const buf = new Uint8Array(width * height);
            for (let z = 0; z < sizeZ; z++) {
                const zSlice = sdf.pixels[z];
                for (let y = 0; y < sizeY; y++) {
                    buf[z * width + y] = zSlice[y * sizeX + sliceIndex];
                }
            }
            sliceData = buf;
        }

        // Pass resx=1, resy=1 so marching squares
        // outputs in voxel-index coordinates.
        const polygons2d: vec2[][] = marchingSquares(
            sliceData,
            width,
            height,
            1,
            1,
            threshold
        );
        if (polygons2d.length === 0) {
            return undefined;
        }

        // Convert marching squares output to 3D voxel coords,
        // then to patient space.
        return polygons2d.map((poly) =>
            poly.map((v) => {
                let voxel: vec3;
                if (sliceAxis === 2) {
                    voxel = vec3.fromValues(v[0], v[1], sliceIndex + 0.5);
                } else if (sliceAxis === 1) {
                    voxel = vec3.fromValues(v[0], sliceIndex + 0.5, v[1]);
                } else {
                    voxel = vec3.fromValues(sliceIndex + 0.5, v[0], v[1]);
                }
                return grid.toWorldSpace(voxel);
            })
        );
    }
}

/** Compute total arc length of a closed polygon. */
function polygonArcLength(poly: vec3[]): number {
    let total = 0;
    const n = poly.length;
    for (let i = 0; i < n; i++) {
        total += vec3.distance(poly[i], poly[(i + 1) % n]);
    }
    return total;
}

/**
 * Cursor overlay that highlights the portion of the contour
 * affected by the gaussian falloff, drawn on the PlaneView's
 * 2D canvas. The affected arc is drawn in a brighter/lighter
 * variant of the structure colour, with small filled circles
 * at the endpoints of the affected range.
 */
class DeformCanvasCursor {
    private spreadPercent: number;
    private readonly color: ReadonlyVec3;
    public lastView?: PlaneView;

    public constructor(spreadPercent: number, color: ReadonlyVec3) {
        this.spreadPercent = spreadPercent;
        this.color = color;
    }

    public setSpreadPercent(spreadPercent: number): void {
        this.spreadPercent = spreadPercent;
    }

    /**
     * Draw the falloff highlight on the contour. The arc
     * near the mouse that corresponds to the spread
     * percentage is drawn in a lighter colour with filled
     * circle endpoints. When {@link falloffInfo} is
     * provided (active deformation), only the endpoint
     * dots are drawn, using the original polygon's arc
     * lengths to locate stable cutoff points.
     *
     * @returns `true` if the mouse is within snap range
     *          of a polygon vertex (or falloffInfo is set).
     */
    public draw(
        view: PlaneView,
        mouseCoords: ReadonlyVec2,
        polygons: vec3[][] | undefined,
        falloffInfo?: DeformFalloffInfo
    ): boolean {
        if (this.lastView && this.lastView !== view) {
            this.clearCanvas(this.lastView);
        }
        this.lastView = view;

        const canvas = view.get2DCanvas;
        if (!canvas) {
            return false;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return false;
        }

        const w = canvas.offsetWidth;
        const h = canvas.offsetHeight;
        if (ctx.canvas.width !== w || ctx.canvas.height !== h) {
            ctx.canvas.width = w;
            ctx.canvas.height = h;
        }

        ctx.clearRect(0, 0, w, h);

        if (!polygons || polygons.length === 0) {
            return false;
        }

        // During active deformation we already know the
        // polygon and anchor; otherwise find them by
        // proximity to the mouse.
        let bestPi: number;
        let bestVi: number;

        if (falloffInfo) {
            bestPi = falloffInfo.polygonIndex;
            bestVi = falloffInfo.anchorIndex;
        } else {
            const patientPos = view.clientToPatientCoordinates(mouseCoords);
            const snapRadiusSq = SNAP_RADIUS_MM * SNAP_RADIUS_MM;

            let bestDistSq = Infinity;
            bestPi = -1;
            bestVi = -1;

            for (let pi = 0; pi < polygons.length; pi++) {
                const poly = polygons[pi];
                for (let vi = 0; vi < poly.length; vi++) {
                    const distSq = vec3.squaredDistance(poly[vi], patientPos);
                    if (distSq <= snapRadiusSq && distSq < bestDistSq) {
                        bestDistSq = distSq;
                        bestPi = pi;
                        bestVi = vi;
                    }
                }
            }

            if (bestPi < 0) {
                return false;
            }
        }

        // The polygon used to compute arc-length cutoffs:
        // original (undeformed) during active deformation,
        // or the current polygon during hover.
        const arcPoly = falloffInfo
            ? falloffInfo.originalPolygon
            : polygons[bestPi];
        // The polygon used to read actual vertex positions
        // (deformed during active deformation).
        const posPoly = polygons[bestPi];
        const n = arcPoly.length;
        const totalArc = polygonArcLength(arcPoly);
        // Half of the affected arc in mm.
        const halfArc = (totalArc * this.spreadPercent) / 200;

        // Brighter/lighter variant of the structure colour.
        const r = Math.min(255, Math.round(this.color[0] * 255) + 80);
        const g = Math.min(255, Math.round(this.color[1] * 255) + 80);
        const b = Math.min(255, Math.round(this.color[2] * 255) + 80);
        const highlightColor = `rgb(${r}, ${g}, ${b})`;

        ctx.strokeStyle = highlightColor;
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Collect CW endpoint.
        let cwEndPoint: vec3 = posPoly[bestVi];
        let arcCw = 0;
        if (!falloffInfo) {
            ctx.beginPath();
            const startCanvas = view.canvasCoordsFromPatientCoords(
                posPoly[bestVi]
            );
            ctx.moveTo(startCanvas[0], startCanvas[1]);
        }

        for (let d = 0; d < n; d++) {
            const curIdx = (bestVi + d) % n;
            const nextIdx = (bestVi + d + 1) % n;
            const segLen = vec3.distance(arcPoly[curIdx], arcPoly[nextIdx]);

            if (arcCw + segLen >= halfArc) {
                // Interpolate the endpoint within this segment.
                const remaining = halfArc - arcCw;
                const t = segLen > 0 ? remaining / segLen : 0;
                cwEndPoint = vec3.lerp(
                    vec3.create(),
                    posPoly[curIdx],
                    posPoly[nextIdx],
                    t
                );
                if (!falloffInfo) {
                    const endCanvas =
                        view.canvasCoordsFromPatientCoords(cwEndPoint);
                    ctx.lineTo(endCanvas[0], endCanvas[1]);
                }
                break;
            }

            arcCw += segLen;
            if (!falloffInfo) {
                const nextCanvas = view.canvasCoordsFromPatientCoords(
                    posPoly[nextIdx]
                );
                ctx.lineTo(nextCanvas[0], nextCanvas[1]);
            }

            // If we've walked the full polygon, stop.
            if (d === n - 1) {
                cwEndPoint = posPoly[nextIdx];
            }
        }
        if (!falloffInfo) {
            ctx.stroke();
        }

        // Collect CCW endpoint.
        let ccwEndPoint: vec3 = posPoly[bestVi];
        let arcCcw = 0;
        if (!falloffInfo) {
            const startCanvas = view.canvasCoordsFromPatientCoords(
                posPoly[bestVi]
            );
            ctx.beginPath();
            ctx.moveTo(startCanvas[0], startCanvas[1]);
        }

        for (let d = 0; d < n; d++) {
            const curIdx = (bestVi - d + n) % n;
            const prevIdx = (bestVi - d - 1 + n) % n;
            const segLen = vec3.distance(arcPoly[curIdx], arcPoly[prevIdx]);

            if (arcCcw + segLen >= halfArc) {
                const remaining = halfArc - arcCcw;
                const t = segLen > 0 ? remaining / segLen : 0;
                ccwEndPoint = vec3.lerp(
                    vec3.create(),
                    posPoly[curIdx],
                    posPoly[prevIdx],
                    t
                );
                if (!falloffInfo) {
                    const endCanvas =
                        view.canvasCoordsFromPatientCoords(ccwEndPoint);
                    ctx.lineTo(endCanvas[0], endCanvas[1]);
                }
                break;
            }

            arcCcw += segLen;
            if (!falloffInfo) {
                const prevCanvas = view.canvasCoordsFromPatientCoords(
                    posPoly[prevIdx]
                );
                ctx.lineTo(prevCanvas[0], prevCanvas[1]);
            }

            if (d === n - 1) {
                ccwEndPoint = posPoly[prevIdx];
            }
        }
        if (!falloffInfo) {
            ctx.stroke();
        }

        // Draw filled circle endpoints.
        ctx.fillStyle = highlightColor;
        const dotRadius = 4;

        const cwCanvas = view.canvasCoordsFromPatientCoords(cwEndPoint);
        ctx.beginPath();
        ctx.arc(cwCanvas[0], cwCanvas[1], dotRadius, 0, 2 * Math.PI);
        ctx.fill();

        const ccwCanvas = view.canvasCoordsFromPatientCoords(ccwEndPoint);
        ctx.beginPath();
        ctx.arc(ccwCanvas[0], ccwCanvas[1], dotRadius, 0, 2 * Math.PI);
        ctx.fill();

        return true;
    }

    public clear(): void {
        if (this.lastView) {
            this.clearCanvas(this.lastView);
            this.lastView = undefined;
        }
    }

    private clearCanvas(view: PlaneView): void {
        const canvas = view.get2DCanvas;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        }
    }
}
